import {makeObservable, override, runInAction} from "mobx";
import {GridColDef, GridSortModel} from "@mui/x-data-grid-premium";

import ProfileStore from "../../../stores/ProfileStore.tsx";
import AuthStore from "../../../authentication/AuthStore.tsx";
import {
    BaseMuixTableController,
} from "../../../components/muix-data-grid/controllers/BaseMuixTableController.tsx";
import {JSONSerializableGridColDef} from "../../../components/muix-data-grid/MuiXColumnDefinitions.ts";
import {Optional} from "../../../utils/ts-utils.ts";
import {ApiCategory, categoryExportString} from "../../../services/classes/Category.ts";
import {CategoryReadonlyCell} from "../../../components/muix-data-grid/cells/CategoryReadonlyCell.tsx";
import {CurrencyCell} from "../../../components/muix-data-grid/cells/CurrencyCell.tsx";
import {CategoryL1Cell} from "../../../components/muix-data-grid/cells/CategoryL1Cell.tsx";
import {BagStore} from "../../../stores/BagStore.tsx";

export class GRTableController extends BaseMuixTableController {

    constructor(
        private auth: AuthStore,
        private profile: ProfileStore,
        protected bagStore: BagStore,
    ) {
        super(bagStore);
        makeObservable(this, {
            tableColumns: override,
            tableAggregationOnlyColumns: override,
            defaultSortModel: override,
        });
        // Override defaults
        // Grouping & visibility should preserve user's configuration
        if (this.rowGroupingModel.length === 0) {
            runInAction(() => {
                this.muixGridGroupModel = ['cluster_name', 'p_description', 's_name'];
                this.storeGroupingModelChange(this.muixGridGroupModel);
            });
        } else {
            runInAction(() => this.muixGridGroupModel = this.getGroupingModel());
        }
        const vm = this.muiXGridColumnVisibilityModel ?? this.getColumnVisibilityModel();
        if (Object.keys(vm).length === 0) {
            runInAction(() => {
                this.muiXGridColumnVisibilityModel = {
                    's_address': false,
                    's_city': false,
                    's_country': false,
                    'p_local_spend': false
                };
                this.storeColumnVisibilityModelChange(this.muiXGridColumnVisibilityModel);
            });
        } else {
            runInAction(() => this.muiXGridColumnVisibilityModel = this.getColumnVisibilityModel());
        }
        // Aggregation model is forced, user cannot change it
        runInAction(() => this.muixGridAggregationModel = {
            p_spend: 'sum',
            p_local_spend: 'sum',
            p_unit_spend: 'sum',
            p_quantity: 'sum',
        });
    }

    get tableColumns(): GridColDef[] {
        const columns: JSONSerializableGridColDef[] = [{
            field: 'cluster_name',
            serializableType: 'string',
            headerName: 'Cluster Name',
            groupable: true,
            sortable: !this.isGroupBy,
        }, {
            field: 'p_name',
            serializableType: 'string',
            headerName: this.profile.partDataName || 'Name',
            groupable: false,
            sortable: !this.isGroupBy,
        }, {
            field: 'p_description',
            serializableType: 'string',
            headerName: this.profile.partDataDescription,
            groupable: true,
            sortable: !this.isGroupBy,
        }, {
            field: 'p_context_1',
            serializableType: 'string',
            headerName: this.profile.partContextColNames.at(0)?.colName || 'Context',
            groupable: false,
            sortable: !this.isGroupBy,
        }, {
            field: 'p_unit_spend',
            serializableType: 'number-currency',
            type: 'number',
            headerName: 'Unit spend',
            groupable: false,
            renderCell: CurrencyCell,
        }, {
            field: 'p_quantity',
            serializableType: 'number',
            type: 'number',
            headerName: 'Quantity',
            groupable: false,
        }, {
            field: 'p_spend',
            serializableType: 'number-currency',
            type: 'number',
            headerName: 'Spend',
            groupable: false,
            renderCell: CurrencyCell,
        }, {
            field: 'p_local_spend',
            serializableType: 'number-currency',
            type: 'number',
            headerName: 'Local price',
            groupable: false,
        }, {
            field: 's_name',
            serializableType: 'string',
            headerName: this.profile.supplierDataName,
            groupable: true,
            sortable: !this.isGroupBy,
        }, {
            field: 's_address',
            serializableType: 'string',
            headerName: 'Address',
            groupable: false,
            sortable: !this.isGroupBy,
        }, {
            field: 's_country',
            serializableType: 'string',
            headerName: 'Country',
            groupable: false,
            sortable: !this.isGroupBy,
        }, {
            field: 's_city',
            serializableType: 'string',
            headerName: 'City',
            groupable: false,
            sortable: !this.isGroupBy,
        }, {
            field: 'bu_name',
            serializableType: 'string',
            headerName: this.profile.buDataName,
            groupable: false,
            sortable: !this.isGroupBy,
        }, {
            field: 'cat',
            serializableType: 'custom-cat',
            headerName: 'Category',
            groupable: false,
            minWidth: 200,
            renderCell: CategoryReadonlyCell,
            renderHeaderFilter: () => null,
            sortable: false,
            valueFormatter: (value: Optional<ApiCategory, 'id'>) => categoryExportString(value),
        }].map(
            c => ({...c, aggregable: !!c['aggregable']})
        ) as JSONSerializableGridColDef[];
        return this.applyColumnOrdering(
            this.applyColumnSettings(
                columns.concat(this.tableAggregationOnlyColumns)
            )
        );
    }

    get tableAggregationOnlyColumns(): JSONSerializableGridColDef[] {
        const columns = [{
            field: 'cat_l1_lst',
            serializableType: 'string',
            headerName: 'Unique L1',
            minWidth: 200,
            renderCell: CategoryL1Cell,
            sortable: false,
        }, {
            field: 'supplier_id_cnt',
            serializableType: 'number',
            headerName: 'Unique Suppliers',
        }, {
            field: 'bu_id_cnt',
            serializableType: 'number',
            headerName: 'Unique BUs',
        }, {
            field: 'part_id_cnt',
            serializableType: 'number',
            headerName: 'Unique transactions',
        }, {
            field: 'part_p_name_cnt',
            serializableType: 'number',
            headerName: 'Unique SKU',
        }] as JSONSerializableGridColDef[];
        return columns.map(c => ({
            ...c,
            pinnable: false,
            filterable: false,
            groupable: false,
            aggregable: false,
        }));
    }

    get defaultSortModel(): GridSortModel | undefined {
        return [{field: 'p_spend', sort: 'desc'}];
    }
}